import { useState, type ReactNode } from "react";
import { RequireAuth, signUpUrl } from "@jkast90/kast-common/auth";
import {
  BypassEmailGate,
  Spinner,
  authScreens,
  isAuthError,
} from "@jkast90/kast-common/ui";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { loadAllWorkspaces } from "../../store/slices/workspaceSlice";
import { getAuthEnabled } from "../../settings";
import { bypassEmail } from "../../auth/bypassEmail";
import { startSignIn } from "../../auth/session";
import { signOutAndLeave } from "../../auth/userSession";
import ErrorMessage from "./ErrorMessage";
import { SIGN_IN_DETAIL } from "./signInText";

/** The gate's text, as the old email prompt had it (its intro and its note; the gate has one slot, under the title). */
const EMAIL_NOTE =
  "Authentication is disabled. Please enter your email address to continue using the application. This email will be used to identify your data and preferences. You can change it later in Settings.";

interface Props {
  children: ReactNode;
  /**
   * The page shows the workspace list's data (true for every page but the
   * reference guides): a failed list load with nothing cached is shown as an
   * error here rather than as an empty page.
   */
  needsWorkspaces?: boolean;
}

/**
 * The sign-in gate (standard 7), on the shared module's RequireAuth. While
 * the session is unknown it shows a neutral wait, never a page that would
 * read as "no workspaces". Signed out, it goes to the login site, showing
 * the sign-in prompt meanwhile. Signed in, a workspace load that failed
 * because the session is gone shows that, not an empty page. With auth off
 * (local testing) the bypass email stands in for the user.
 */
export default function ProtectedRoute({
  children,
  needsWorkspaces = true,
}: Props) {
  const dispatch = useAppDispatch();
  // Read once on mount: the auth settings only change with a reload.
  const [authEnabled] = useState(getAuthEnabled);
  const workspaceError = useAppSelector(
    (state) => state.workspace.operations?.list?.error,
  );
  const workspaceCount = useAppSelector(
    (state) => state.workspace.workspaces.length,
  );

  const screens = authScreens({
    signIn: {
      onSignIn: startSignIn,
      autoSignIn: true,
      signUpHref: signUpUrl(),
      detail: SIGN_IN_DETAIL,
    },
    loading: <Spinner showLabel label="Checking your session…" />,
  });

  let page: ReactNode = children;
  if (workspaceError && isAuthError(workspaceError)) {
    page = (
      <ErrorMessage
        error={workspaceError}
        isLoggedIn={true}
        onLogout={() => void signOutAndLeave(startSignIn)}
      />
    );
  } else if (needsWorkspaces && workspaceError && workspaceCount === 0) {
    page = (
      <ErrorMessage
        error={workspaceError}
        isLoggedIn={true}
        onRetry={() => dispatch(loadAllWorkspaces({ forceRefresh: true }))}
      />
    );
  }

  return (
    <RequireAuth
      authEnabled={authEnabled}
      whenAuthOff={
        <BypassEmailGate
          store={bypassEmail}
          title="Email Required"
          label="Email Address:"
          description={EMAIL_NOTE}
        >
          {children}
        </BypassEmailGate>
      }
      loading={screens.loading}
      prompt={(state) => (
        <div data-testid="sign-in-prompt">{screens.prompt(state)}</div>
      )}
    >
      {page}
    </RequireAuth>
  );
}
